import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../Assets/logo.png';

const Main = styled.nav`
  height: 60px;
  width: 100%;
  background: linear-gradient(135deg, #0283e5 0%, #026bc0 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  position: sticky;
  top: 0;
  z-index: 1000;
  padding-top: env(safe-area-inset-top, 0px);
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 0 12px;
  }
`;

/* Fixed equal widths on the outer sections so the centered Brand stays
   visually centered regardless of whether the menu button is present. */
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  min-width: 44px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  min-width: 44px;
`;

const MenuButton = styled.button`
  background: transparent;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.12s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255,255,255,0.18);
  }
  &:active {
    transform: scale(0.92);
    background: rgba(255,255,255,0.28);
  }
`;

const Brand = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 600;
  gap: 10px;
  white-space: nowrap;
  letter-spacing: 0.2px;

  @media (max-width: 600px) {
    font-size: 1rem;
    gap: 6px;
  }
`;

const PageTitle = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255,255,255,0.92);
  background: rgba(255,255,255,0.14);
  padding: 5px 12px;
  border-radius: 999px;

  @media (max-width: 700px) {
    display: none;
  }
`;

const LogoImg = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  object-fit: cover;
  box-shadow: 0 1px 4px rgba(0,0,0,0.25);

  @media (max-width: 600px) {
    width: 30px;
    height: 30px;
  }
`;

// Turn a route segment like "consumption-report" or "meterDetails" into "Consumption Report" / "Meter Details"
const prettifyPageName = (segment) => {
  if (!segment) return 'Home';
  return segment
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase -> spaced
    .replace(/[-_]+/g, ' ')               // kebab/snake -> spaced
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const Navbar = ({ showMenuButton, toggleSidebar }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/';
  const lastSegment = location.pathname.split('/').filter(Boolean).pop();
  const formattedPageName = prettifyPageName(lastSegment);

  if (isLoginPage) return <Main aria-hidden="true" style={{ boxShadow: 'none' }} />;

  return (
    <Main>
      <Container>
        <LeftSection>
          {showMenuButton && (
            <MenuButton onClick={toggleSidebar} aria-label="Toggle Sidebar">
              <MenuIcon style={{ fontSize: '1.7rem' }} />
            </MenuButton>
          )}
        </LeftSection>

        <Brand>
          <LogoImg src={Logo} alt="Aqua Smart Meter logo" />
          Aqua Smart Meter
        </Brand>

        <RightSection>
          <PageTitle>{formattedPageName}</PageTitle>
        </RightSection>
      </Container>
    </Main>
  );
};

export default Navbar;